import {
  Alert,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import axios, { AxiosResponse } from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { API_ENDPOINT } from '../../configurations/global.config';
import { AuthContext } from '../../contexts/user-context/user-context';
import { generateQRCode } from '../common/task-barcode-printer/task-barcode-printer';
import { decode } from '../../utils/encoding';
import CustomDialog from '../common/custom-dialog/custom-dialog';
import { IPickupTask } from '../../interfaces/delivery-company/tasks';
import { DriverType } from '../../interfaces/delivery-company/drivers';
import { useSnackBar } from '../../contexts/snackbar';
import dayjs from 'dayjs';
import { displayLocation } from '../../utils/locations';
import { MerchantWithIcon } from '../common/styles/merchant-with-icon';
import { showPickupTaskState } from '../common/styles/pickup-task.styles';
import { DriverWithIcon } from '../common/styles/driver-with-icon';
import { showWorkflow } from '../common/styles/workflow-display';
import { Workflow } from '../../enums/orders';
import { CustomerWithIcon } from '../common/styles/customer-with-icon';
import CustomerWithNavigate from '../common/customer-with-navigate/customer-with-navigate';
import { UserType } from '../../enums/users';
import PickupTaskDialog from '../delivery-company-components/pickup-tasks/pickup-task-dialog';
import PickupTaskQuickScanDialog from '../driver-components/quick-scan-dialogs/pickup-task-dialog';

interface TemporaryPackageLookupDialogProps {
  id: string;
  onCloseDialog: () => void;
}

interface TemporaryPackage {
  id: number;
  package_id: string;
  pickup_task: IPickupTask;
  order: number; // order pk
  created_at: string;
  created_by: DriverType;
}

function TemporaryPackageLookupDialog({
  id,
  onCloseDialog,
}: TemporaryPackageLookupDialogProps) {
  const [temporaryPackage, setTemporaryPackage] =
    React.useState<TemporaryPackage>();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [response, setResponse] = React.useState<AxiosResponse>();
  const [message, setMessage] = React.useState<string>('');
  const [qrCode, setQRCode] = useState('');
  const { showMessage } = useSnackBar();
  const [showingPickupTask, setShowingPickupTask] = useState(false);

  const { user, config } = useContext(AuthContext);

  useEffect(() => {
    generateQRCode(id).then(setQRCode);
  }, [id]);

  useEffect(() => {
    const fetchAndSetPackage = async () => {
      setIsLoading(true);
      try {
        const endpoint = `${API_ENDPOINT}/api/v1/temporary-package/${encodeURIComponent(id)}/`;
        const response = await axios.get<TemporaryPackage>(endpoint, config);
        setResponse(response);
        setTemporaryPackage(response.data);
        showMessage('Package Found!');
      } catch (e) {
        setTemporaryPackage(undefined);
        showMessage('Package not found', 'error');
        if (axios.isAxiosError(e)) {
          setMessage('Package not found: ' + e.message);
        } else {
          setMessage('Package not found');
        }
      }
      setIsLoading(false);
    };
    fetchAndSetPackage();
  }, [id]);

  const resetState = () => {
    setIsLoading(false);
    setTemporaryPackage(undefined);
    setResponse(undefined);
  };

  return (
    <div>
      <CustomDialog fullWidth open={true} onClose={onCloseDialog}>
        <DialogTitle>
          <div
            style={{
              textAlign: 'right',
              width: '100%',
            }}
          >
            <IconButton
              onClick={() => {
                resetState();
                onCloseDialog();
              }}
            >
              <ClearIcon color="primary" fontSize="large" />
            </IconButton>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            {id !== '' && (
              <Box
                component={'img'}
                src={qrCode}
                sx={{ height: 200, width: 200 }}
              />
            )}
            <>{decode(id).id}</>
            {temporaryPackage && (
              <DriverWithIcon
                centered
                driver={temporaryPackage.created_by}
                showPhone
              />
            )}
          </div>
        </DialogTitle>
        <DialogContent>
          {isLoading ? (
            <LinearProgress />
          ) : (
            <>
              {message !== '' && (
                <Alert
                  severity={response && response?.status>=200 && response?.status < 400 ? 'success' : 'error'}
                >
                  {message}
                </Alert>
              )}

              {temporaryPackage && (
                <Paper elevation={0} sx={{ padding: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Package Information
                      </Typography>
                      <Divider />

                      <Typography>
                        <strong>Scanned At:</strong>{' '}
                        {dayjs(temporaryPackage.created_at).format(
                          'DD-MM-YYYY HH:mm'
                        )}
                      </Typography>
                      <Typography>
                        <div
                          style={{
                            display: 'flex',
                            gap: 2,
                            padding: 0,
                            margin: 0,
                            alignItems: 'center',
                          }}
                        >
                          <strong> Merchant:</strong>{' '}
                          <MerchantWithIcon
                            merchant={temporaryPackage.pickup_task.merchant}
                          />
                        </div>
                      </Typography>

                      {temporaryPackage.order && (
                        <Typography>
                          <strong style={{ color: 'red' }}>
                            Exchange For:
                          </strong>{' '}
                          <a
                            href={`/order/${temporaryPackage.order}/`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {' '}
                            <u>
                              <b>Order #1{temporaryPackage.order}</b>
                            </u>
                          </a>
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Pickup Task #{temporaryPackage.pickup_task.id}
                      </Typography>
                      <Divider />
                      <div
                        style={{
                          display: 'flex',
                          gap: 2,
                          alignItems: 'center',
                        }}
                      >
                        {showPickupTaskState(
                          temporaryPackage.pickup_task.status
                        )}
                        {showWorkflow(
                          temporaryPackage.pickup_task.workflow,
                          false,
                          undefined,
                          ""
                        )}
                      </div>
                      <br />
                      <strong>Pickup Location</strong>
                      <br />
                      {temporaryPackage.pickup_task.workflow ===
                        Workflow.RETURN && (
                        <>
                          <CustomerWithNavigate
                            customer={temporaryPackage.pickup_task.customer}
                            location={
                              temporaryPackage.pickup_task.customer_location
                            }
                          />
                        </>
                      )}
                      {temporaryPackage.pickup_task.workflow ===
                        Workflow.STANDARD &&
                        displayLocation(
                          temporaryPackage.pickup_task.merchant_location
                        )}
                    </Grid>
                  </Grid>
                  <div
                    style={{
                      width: '100%',
                      textAlign: 'center',
                      marginTop: 10,
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setShowingPickupTask(true)}
                    >
                      See More
                    </Button>
                  </div>

                  {showingPickupTask && (
                    <>
                      {user?.type === UserType.DRIVER ? (
                        <PickupTaskQuickScanDialog
                          task={temporaryPackage.pickup_task}
                          onClose={() => setShowingPickupTask(false)}
                        />
                      ) : (
                        <PickupTaskDialog
                          taskID={temporaryPackage.pickup_task.id}
                          onClose={() => setShowingPickupTask(false)}
                        />
                      )}
                    </>
                  )}
                </Paper>
              )}
            </>
          )}
        </DialogContent>
      </CustomDialog>
    </div>
  );
}

export default TemporaryPackageLookupDialog;
